/**
 * Cliente de API.
 *
 * Um caminho só para toda chamada, com três responsabilidades: pôr o token,
 * traduzir o envelope de erro, e renovar a sessão no 401 — UMA vez.
 */
export class ErroApi extends Error {
  constructor(
    readonly codigo: string,
    mensagem: string,
    readonly status: number,
    readonly detalhes?: unknown,
  ) {
    super(mensagem);
    this.name = 'ErroApi';
  }
}

interface EnvelopeErro {
  erro?: { codigo?: string; mensagem?: string; detalhes?: unknown };
}

export interface Dependencias {
  buscar: typeof fetch;
  obterAcesso: () => string | null;
  /** Renova e devolve o novo access, ou lança se a sessão morreu. */
  aoRenovar: () => Promise<string>;
}

export function criarCliente(dep: Dependencias) {
  async function chamar(
    caminho: string,
    init: RequestInit,
    jaRenovou = false,
  ): Promise<Response> {
    const acesso = dep.obterAcesso();
    const resposta = await dep.buscar(`/api${caminho}`, {
      ...init,
      headers: {
        ...(init.headers as Record<string, string> | undefined),
        ...(acesso ? { authorization: `Bearer ${acesso}` } : {}),
      },
    });

    // `jaRenovou` é o teto contra o laço: sessão morta de verdade devolve 401 à
    // renovação também, e sem o teto o navegador gira para sempre.
    if (resposta.status === 401 && !jaRenovou) {
      await dep.aoRenovar();
      return chamar(caminho, init, true);
    }
    return resposta;
  }

  async function corpo<T>(resposta: Response): Promise<T> {
    if (resposta.ok) return (await resposta.json()) as T;
    const envelope = (await resposta.json().catch(() => ({}))) as EnvelopeErro;
    throw new ErroApi(
      envelope.erro?.codigo ?? 'desconhecido',
      envelope.erro?.mensagem ?? 'Não foi possível concluir. Tente de novo.',
      resposta.status,
      envelope.erro?.detalhes,
    );
  }

  return {
    async get<T>(caminho: string): Promise<T> {
      return corpo<T>(await chamar(caminho, { method: 'GET' }));
    },
    async post<T>(caminho: string, dados?: unknown): Promise<T> {
      return corpo<T>(
        await chamar(caminho, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          ...(dados === undefined ? {} : { body: JSON.stringify(dados) }),
        }),
      );
    },
    /**
     * `PATCH` com o objeto inteiro — ver o `atualizarPessoaSchema` no shared.
     *
     * Compartilha o `chamar`, então herda de graça o que importa: o cabeçalho
     * de autorização, a renovação de token no 401 e a tradução do erro em
     * `ErroApi`. Um `fetch` solto aqui perderia os três de uma vez.
     */
    async patch<T>(caminho: string, dados?: unknown): Promise<T> {
      return corpo<T>(
        await chamar(caminho, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          ...(dados === undefined ? {} : { body: JSON.stringify(dados) }),
        }),
      );
    },
    /** Baixa binário. Não passa por `corpo` — o retorno não é JSON. */
    async baixar(caminho: string): Promise<Blob> {
      const resposta = await chamar(caminho, { method: 'GET' });
      if (!resposta.ok) return corpo<never>(resposta);
      return resposta.blob();
    },
  };
}

export type Cliente = ReturnType<typeof criarCliente>;
